import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API_URL } from "./config";
import { wowheadItemUrl } from "./wowhead";
import AddonExportPanel from "./AddonExportPanel";

type MemberStub = { id: string; name: string };

type PreviewRow = {
  index: number;
  player: string;
  character_id: string | null;
  match_status: string;
  match_candidates: { id: string; name: string }[];
  item_id: string;
  item_name: string;
  loot_item_id: number | null;
  response: string | null;
  response_known: boolean;
  votes: number | null;
  awarded_at: string | null;
  parse_error: string | null;
};

type Props = {
  guildId: string;
  gameCode?: string | null;
  members: MemberStub[];
};

const TAG_OPTIONS = [
  { value: "bis", label: "BIS" },
  { value: "upgrade", label: "Upgrade" },
  { value: "sidegrade", label: "Sidegrade" },
  { value: "offspec", label: "Off-spec" },
] as const;

export default function GuildLootImportsTab({ guildId, gameCode, members }: Props) {
  const [format, setFormat] = useState<"rclc_csv" | "rclc_json">("rclc_csv");
  const [payload, setPayload] = useState("");
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [previewId, setPreviewId] = useState<string | null>(null);
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  const [unknownResponses, setUnknownResponses] = useState<string[]>([]);
  const [canCommit, setCanCommit] = useState(false);

  const [rowCharacter, setRowCharacter] = useState<Record<number, string>>({});
  const [rowSkip, setRowSkip] = useState<Record<number, boolean>>({});
  const [responseMapDraft, setResponseMapDraft] = useState<Record<string, string>>({});

  const [existingMappings, setExistingMappings] = useState<{ response_text: string; normalized_tag: string }[]>([]);

  const loadMappings = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/guilds/${guildId}/loot-response-mappings`, {
        credentials: "include",
      });
      if (!res.ok) return;
      const data = await res.json();
      setExistingMappings(Array.isArray(data.mappings) ? data.mappings : []);
    } catch {
      /* ignore */
    }
  }, [guildId]);

  useEffect(() => {
    loadMappings();
  }, [loadMappings]);

  const runPreview = async () => {
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/guilds/${guildId}/loot-awards/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ format, payload }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setPreviewId(data.previewId || null);
      const rows: PreviewRow[] = Array.isArray(data.rows) ? data.rows : [];
      setPreviewRows(rows);
      setUnknownResponses(Array.isArray(data.unknownResponses) ? data.unknownResponses : []);
      setCanCommit(!!data.canCommit);
      setRowCharacter({});
      // Auto-skip rows that already exist as awards. Officer can uncheck to
      // force a duplicate insert (e.g. same item legitimately awarded twice).
      const initialSkip: Record<number, boolean> = {};
      for (const r of rows) {
        if (r.match_status === "existing") initialSkip[r.index] = true;
      }
      setRowSkip(initialSkip);
      const drafts: Record<string, string> = {};
      (data.unknownResponses || []).forEach((r: string) => {
        drafts[r] = "bis";
      });
      setResponseMapDraft(drafts);
    } catch (e: any) {
      setPreviewId(null);
      setPreviewRows([]);
      setError(e?.message || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const buildRowActions = () => {
    const rowActions: { index: number; skip?: boolean; characterId?: string }[] = [];
    previewRows.forEach((row) => {
      const ch = rowCharacter[row.index];
      const skip = rowSkip[row.index];
      if (skip) rowActions.push({ index: row.index, skip: true });
      else if (ch && (row.match_status === "unmatched" || row.match_status === "ambiguous")) {
        rowActions.push({ index: row.index, characterId: ch });
      }
    });
    return rowActions;
  };

  const buildResponseMappings = () => {
    return unknownResponses
      .filter((r) => responseMapDraft[r])
      .map((r) => ({ responseText: r, normalizedTag: responseMapDraft[r] }));
  };

  const commitValid = useMemo(() => {
    if (!previewId || previewRows.length === 0) return false;
    for (const r of previewRows) {
      if (rowSkip[r.index]) continue;
      if (r.parse_error) return false;
      if (r.match_status === "duplicate") return false;
      // Note: 'existing' is NOT a hard blocker — auto-skipped by default but
      // an un-skipped row will commit as an intentional override.
      if ((r.match_status === "unmatched" || r.match_status === "ambiguous") && !rowCharacter[r.index] && !r.character_id) {
        return false;
      }
      const resp = (r.response || "").trim();
      if (resp && !r.response_known && !responseMapDraft[resp]) return false;
    }
    return true;
  }, [previewId, previewRows, rowSkip, rowCharacter, responseMapDraft]);

  /** Grouped counts so officers see what's blocking import at a glance. */
  const importChecklist = useMemo(() => {
    if (previewRows.length === 0) {
      return null;
    }
    let skipped = 0;
    let parseBlock = 0;
    let dup = 0;
    let existing = 0;
    let needChar = 0;
    let needResponseRow = 0;
    let ready = 0;
    for (const r of previewRows) {
      if (rowSkip[r.index]) {
        // 'existing' rows are auto-skipped on preview load — track them
        // separately from manual skips so officers see "X already in ledger".
        if (r.match_status === "existing") existing++;
        else skipped++;
        continue;
      }
      if (r.parse_error) {
        parseBlock++;
        continue;
      }
      if (r.match_status === "duplicate") {
        dup++;
        continue;
      }
      if (
        (r.match_status === "unmatched" || r.match_status === "ambiguous") &&
        !rowCharacter[r.index] &&
        !r.character_id
      ) {
        needChar++;
        continue;
      }
      const resp = (r.response || "").trim();
      if (resp && !r.response_known && !responseMapDraft[resp]) {
        needResponseRow++;
        continue;
      }
      ready++;
    }
    return {
      total: previewRows.length,
      skipped,
      parseBlock,
      dup,
      existing,
      needChar,
      needResponseRow,
      unknownLabels: unknownResponses.length,
      ready,
    };
  }, [previewRows, rowSkip, rowCharacter, responseMapDraft, unknownResponses.length]);

  const commit = async () => {
    if (!previewId) return;
    setError(null);
    setSuccess(null);
    setCommitting(true);
    try {
      const responseMappings = buildResponseMappings();
      const res = await fetch(`${API_URL}/guilds/${guildId}/loot-awards/import/commit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          previewId,
          rowActions: buildRowActions(),
          responseMappings,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setSuccess(`Imported ${data.inserted ?? 0} award(s).`);
      setPreviewId(null);
      setPreviewRows([]);
      setUnknownResponses([]);
      setPayload("");
      await loadMappings();
    } catch (e: any) {
      setError(e?.message || "Commit failed");
    } finally {
      setCommitting(false);
    }
  };

  const deleteMapping = async (response_text: string) => {
    const res = await fetch(`${API_URL}/guilds/${guildId}/loot-response-mappings`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ response_text }),
    });
    if (res.ok) loadMappings();
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-200 max-w-5xl">
      {/* Outbound first: getting the roster's lists into the raid is what an
          officer opens this tab for on raid night. Imports come after. */}
      <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-4 space-y-3">
        <div>
          <h3 className="text-sm font-bold text-[var(--c-ink)] uppercase tracking-widest mb-1">BiS to your loot addon</h3>
          <p className="text-xs text-[var(--c-ink-3)] leading-relaxed max-w-2xl">
            Every raider's ranked list in one string, in the order they set on their Prio tab. Re-copy it whenever
            people change their lists: nothing syncs on its own.
          </p>
        </div>
        <AddonExportPanel endpointBase={`${API_URL}/guilds/${guildId}/bis/export`} subject="this guild" />
      </div>

      <div>
        <h3 className="text-sm font-bold text-[var(--c-ink)] uppercase tracking-widest mb-1">Loot imports</h3>
        <p className="text-xs text-[var(--c-ink-3)] leading-relaxed max-w-2xl">
          Paste an RCLootCouncil CSV or JSON export. Review the preview, map any new response labels once, resolve unmatched
          characters, then confirm to write awards. Players see their history on the roster gear modal.
        </p>
      </div>

      <div className="rounded-xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-4 space-y-3">
        <div className="flex flex-wrap gap-3 items-center">
          <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--c-ink-3)]">Format</label>
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value as "rclc_csv" | "rclc_json")}
            className="rounded-lg border border-[var(--c-line-strong)] bg-[var(--c-surface)] px-3 py-2 text-xs text-[var(--c-ink)]"
          >
            <option value="rclc_csv">RCLC CSV</option>
            <option value="rclc_json">RCLC JSON</option>
          </select>
        </div>
        <textarea
          value={payload}
          onChange={(e) => setPayload(e.target.value)}
          placeholder="Paste export here…"
          rows={10}
          className="w-full rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] px-3 py-2.5 text-xs text-[var(--c-ink)] placeholder:text-[var(--c-ink-4)] font-mono leading-relaxed focus:outline-none focus:border-amber-500/40"
        />
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={loading || !payload.trim()}
            onClick={runPreview}
            className="rounded-lg bg-amber-500/20 border border-amber-500/35 px-4 py-2 text-xs font-bold text-amber-800 dark:text-amber-100 hover:bg-amber-500/30 disabled:opacity-40"
          >
            {loading ? "Parsing…" : "Run preview"}
          </button>
          {previewRows.length > 0 && (
            <button
              type="button"
              disabled={committing || !commitValid}
              onClick={commit}
              className="rounded-lg bg-emerald-500/20 border border-emerald-500/35 px-4 py-2 text-xs font-bold text-emerald-800 dark:text-emerald-100 hover:bg-emerald-500/30 disabled:opacity-40"
            >
              {committing ? "Saving…" : "Confirm import"}
            </button>
          )}
        </div>
        {error && <div className="text-xs text-red-600/95 dark:text-red-400/95">{error}</div>}
        {success && <div className="text-xs text-emerald-700/95 dark:text-emerald-400/95">{success}</div>}
      </div>

      {unknownResponses.length > 0 && (
        <div className="rounded-xl border border-violet-500/20 bg-violet-500/[0.04] p-4 space-y-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-violet-800/90 dark:text-violet-200/90">Map response labels</div>
          <p className="text-[11px] text-[var(--c-ink-3)]">Each distinct RCLC response string must map to BIS, Upgrade, Sidegrade, or Off-spec before commit.</p>
          <div className="space-y-2">
            {unknownResponses.map((u) => (
              <div key={u} className="flex flex-wrap items-center gap-2 text-xs">
                <span className="font-mono text-[var(--c-ink-2)] px-2 py-1 rounded bg-[var(--c-surface)] border border-[var(--c-line)] max-w-[240px] truncate" title={u}>
                  {u}
                </span>
                <select
                  value={responseMapDraft[u] || "bis"}
                  onChange={(e) => setResponseMapDraft((prev) => ({ ...prev, [u]: e.target.value }))}
                  className="rounded-lg border border-[var(--c-line-strong)] bg-[var(--c-surface)] px-2 py-1 text-xs text-[var(--c-ink)]"
                >
                  {TAG_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}

      {importChecklist && (
        <div className="rounded-xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/[0.06] to-[var(--c-surface)] p-4 space-y-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-cyan-800/90 dark:text-cyan-200/90">Reconcile checklist</div>
          <p className="text-[11px] text-[var(--c-ink-3)] leading-relaxed">
            Fix the buckets below (or skip rows). When everything needed is resolved, <span className="text-[var(--c-ink-2)]">Confirm import</span>{" "}
            unlocks.
          </p>
          <div className="flex flex-wrap gap-2 text-[11px]">
            <span className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1.5 text-emerald-800 dark:text-emerald-100 font-semibold tabular-nums">
              Ready {importChecklist.ready}
            </span>
            {importChecklist.needChar > 0 ? (
              <span className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-2.5 py-1.5 text-rose-800 dark:text-rose-100 font-semibold tabular-nums">
                Name fix {importChecklist.needChar}
              </span>
            ) : null}
            {importChecklist.unknownLabels > 0 ? (
              <span className="rounded-lg border border-violet-500/30 bg-violet-500/10 px-2.5 py-1.5 text-violet-800 dark:text-violet-100 font-semibold tabular-nums">
                New response labels {importChecklist.unknownLabels}
              </span>
            ) : importChecklist.needResponseRow > 0 ? (
              <span className="rounded-lg border border-violet-500/30 bg-violet-500/10 px-2.5 py-1.5 text-violet-800 dark:text-violet-100 font-semibold tabular-nums">
                Rows need response tag {importChecklist.needResponseRow}
              </span>
            ) : null}
            {importChecklist.dup > 0 ? (
              <span className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-amber-800 dark:text-amber-100 font-semibold tabular-nums">
                Duplicates {importChecklist.dup}
              </span>
            ) : null}
            {importChecklist.parseBlock > 0 ? (
              <span className="rounded-lg border border-orange-500/30 bg-orange-500/10 px-2.5 py-1.5 text-orange-800 dark:text-orange-100 font-semibold tabular-nums">
                Parse issues {importChecklist.parseBlock}
              </span>
            ) : null}
            {importChecklist.skipped > 0 ? (
              <span className="rounded-lg border border-[var(--c-line-strong)] bg-[var(--c-sunken)] px-2.5 py-1.5 text-[var(--c-ink-3)] font-semibold tabular-nums">
                Skipped {importChecklist.skipped}
              </span>
            ) : null}
            <span className="rounded-lg border border-[var(--c-line)] px-2.5 py-1.5 text-[var(--c-ink-3)] tabular-nums">Total {importChecklist.total}</span>
          </div>
        </div>
      )}

      {previewRows.length > 0 && (
        <div className="rounded-xl border border-[var(--c-line)] overflow-hidden">
          <div className="px-4 py-2 border-b border-[var(--c-line-soft)] bg-[var(--c-surface)] flex justify-between items-center">
            <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--c-ink-3)]">Preview</span>
            <span className="text-[10px] text-[var(--c-ink-4)]">{previewRows.length} row(s)</span>
          </div>
          <div className="overflow-x-auto max-h-[min(480px,55vh)] overflow-y-auto">
            <table className="w-full text-left text-[11px]">
              <thead className="sticky top-0 bg-[var(--c-surface)] z-10 border-b border-[var(--c-line)]">
                <tr className="text-[var(--c-ink-3)] uppercase tracking-wider">
                  <th className="px-2 py-2">#</th>
                  <th className="px-2 py-2">Player</th>
                  <th className="px-2 py-2">Match</th>
                  <th className="px-2 py-2">Item</th>
                  <th className="px-2 py-2">When</th>
                  <th className="px-2 py-2">Response</th>
                  <th className="px-2 py-2">Fix</th>
                  <th className="px-2 py-2">Skip</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map((r) => {
                  const st =
                  r.match_status === "matched"
                    ? "text-emerald-700/90 dark:text-emerald-400/90"
                    : r.match_status === "duplicate"
                      ? "text-amber-700/90 dark:text-amber-400/90"
                      : r.match_status === "existing"
                        ? "text-sky-700/90 dark:text-sky-400/90"
                        : "text-rose-600/90 dark:text-rose-400/90";
                  return (
                    <tr key={r.index} className="border-b border-[var(--c-line-soft)] hover:bg-[var(--c-bg-veil)]">
                      <td className="px-2 py-1.5 text-[var(--c-ink-4)] tabular-nums">{r.index}</td>
                      <td className="px-2 py-1.5 text-[var(--c-ink)]">{r.player || "—"}</td>
                      <td className={`px-2 py-1.5 font-medium ${st}`}>
                        {r.parse_error || r.match_status}
                        {r.match_status === "ambiguous" && r.match_candidates?.length ? (
                          <span className="block text-[9px] text-[var(--c-ink-3)] font-normal mt-0.5">
                            {r.match_candidates.map((c) => c.name).join(" · ")}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-2 py-1.5 text-[var(--c-ink-2)] max-w-[200px]">
                        {r.item_id ? (
                          <a
                            href={wowheadItemUrl(r.item_id, gameCode)}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sky-700/90 dark:text-sky-300/90 hover:underline truncate block"
                            data-wh-rename-link="false"
                          >
                            {r.item_name || r.item_id}
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-[var(--c-ink-3)] whitespace-nowrap">
                        {r.awarded_at ? new Date(r.awarded_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-2 py-1.5 text-[var(--c-ink-3)] max-w-[120px] truncate" title={r.response || ""}>
                        {r.response || "—"}
                      </td>
                      <td className="px-2 py-1.5">
                        {(r.match_status === "unmatched" || r.match_status === "ambiguous") && (
                          <select
                            value={rowCharacter[r.index] || ""}
                            onChange={(e) =>
                              setRowCharacter((prev) => ({ ...prev, [r.index]: e.target.value }))
                            }
                            className="max-w-[160px] rounded border border-[var(--c-line)] bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] text-[10px] text-[var(--c-ink)] py-1"
                          >
                            <option value="">Select character…</option>
                            {members.map((m) => (
                              <option key={m.id} value={m.id}>
                                {m.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="checkbox"
                          checked={!!rowSkip[r.index]}
                          onChange={(e) =>
                            setRowSkip((prev) => ({ ...prev, [r.index]: e.target.checked }))
                          }
                          className="accent-amber-500"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!canCommit && (
            <div className="px-4 py-2 text-[10px] text-amber-800/80 dark:text-amber-200/80 bg-amber-500/5 border-t border-amber-500/15">
              Resolve unmatched names, map unknown responses, or skip rows before confirming. Duplicate rows in the paste must be skipped.
            </div>
          )}
        </div>
      )}

      {existingMappings.length > 0 && (
        <div className="rounded-xl border border-[var(--c-line-soft)] p-4">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--c-ink-3)] mb-2">Saved response mappings</div>
          <ul className="space-y-1 text-xs text-[var(--c-ink-2)]">
            {existingMappings.map((m) => (
              <li key={m.response_text} className="flex items-center justify-between gap-2 py-1 border-b border-[var(--c-line-soft)] last:border-0">
                <span className="font-mono truncate">{m.response_text}</span>
                <span className="text-[var(--c-ink-3)] shrink-0">{m.normalized_tag}</span>
                <button
                  type="button"
                  onClick={() => deleteMapping(m.response_text)}
                  className="text-[10px] text-red-600/80 dark:text-red-400/80 hover:text-red-700 dark:hover:text-red-300"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
